import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Loader2, MoveUp, MoveDown } from 'lucide-react';
import { Button } from './ui/button';

const GalleryUploader = ({ images = [], onImagesChange }) => {
  const [uploading, setUploading] = useState(false);
  const [currentImages, setCurrentImages] = useState(images);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    try {
      const newImages = [];
      
      for (const file of acceptedFiles) {
        const reader = new FileReader();
        const imageUrl = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        
        newImages.push({
          file,
          preview: imageUrl,
          isNew: true
        });
      }
      
      const updated = [...currentImages, ...newImages];
      setCurrentImages(updated);
      
      if (onImagesChange) {
        onImagesChange(updated);
      }
    } catch (error) {
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
    }
  }, [currentImages, onImagesChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    multiple: true,
    disabled: uploading
  });

  const handleRemove = (index) => {
    const updated = currentImages.filter((_, i) => i !== index);
    setCurrentImages(updated);
    if (onImagesChange) {
      onImagesChange(updated);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const updated = [...currentImages];
    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    setCurrentImages(updated);
    if (onImagesChange) {
      onImagesChange(updated);
    }
  };

  const handleMoveDown = (index) => {
    if (index === currentImages.length - 1) return;
    const updated = [...currentImages];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setCurrentImages(updated);
    if (onImagesChange) {
      onImagesChange(updated);
    }
  };

  return (
    <div className="space-y-4">
      <label className="text-sm font-medium">Gallery Images</label>
      
      {/* Existing images grid */}
      {currentImages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {currentImages.map((img, index) => (
            <div key={index} className="relative group">
              <img 
                src={img.preview || img} 
                alt={`Gallery ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg border-2 border-gray-300"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded-lg flex items-center justify-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                >
                  <MoveUp size={14} />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === currentImages.length - 1}
                >
                  <MoveDown size={14} />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemove(index)}
                >
                  <X size={14} />
                </Button>
              </div>
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload dropzone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragActive 
            ? 'border-[#F4B400] bg-yellow-50' 
            : 'border-gray-300 hover:border-[#F4B400] hover:bg-gray-50'
        }`}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="animate-spin text-[#F4B400] mb-2" size={32} />
            <p className="text-sm text-gray-600">Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="text-gray-400 mb-2" size={28} />
            {isDragActive ? (
              <p className="text-sm text-gray-600">Drop the images here</p>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-1">
                  {currentImages.length > 0 ? 'Add more images' : 'Upload gallery images'}
                </p>
                <p className="text-xs text-gray-500">
                  Drag & drop multiple images or click to select
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryUploader;
